import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Wrench, Plus, Pencil, Trash2, Search, ChevronDown, ChevronRight, Truck } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPOS_RECURSO = ['Vehiculo', 'Maquinaria Pesada', 'Herramienta', 'Instrumento', 'Equipo de Seguridad', 'Comunicaciones', 'Repuesto', 'Apoyo', 'Otro'];
const ESTADOS = ['Operativo', 'En Mantencion', 'Fuera de Servicio', 'Dado de Baja'];
const ESTADO_COLORS = { Operativo: 'bg-emerald-100 text-emerald-700', 'En Mantencion': 'bg-amber-100 text-amber-700', 'Fuera de Servicio': 'bg-red-100 text-red-700', 'Dado de Baja': 'bg-slate-200 text-slate-500' };

export default function CatalogoRecursos() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [recursos, setRecursos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [form, setForm] = useState({ codigo: '', nombre: '', tipo: 'Vehiculo', empresa_id: null, es_mandante: true, marca: '', modelo: '', patente: '', estado: 'Operativo' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resR, resE] = await Promise.all([
        axios.get(`${API}/catalogo-recursos${filtroTipo ? `?tipo=${filtroTipo}` : ''}`, { headers }),
        axios.get(`${API}/empresas-colaboradoras`, { headers })
      ]);
      setRecursos(resR.data); setEmpresas(resE.data);
    } catch { }
    setLoading(false);
  }, [token, filtroTipo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = recursos.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.codigo.toLowerCase().includes(q) || r.nombre.toLowerCase().includes(q) || r.marca.toLowerCase().includes(q) || r.patente.toLowerCase().includes(q);
  });

  const grouped = {};
  filtered.forEach(r => { const k = r.tipo || 'Sin Tipo'; if (!grouped[k]) grouped[k] = []; grouped[k].push(r); });

  const openNew = () => {
    setEditing(null);
    setForm({ codigo: '', nombre: '', tipo: 'Vehiculo', empresa_id: null, es_mandante: true, marca: '', modelo: '', patente: '', estado: 'Operativo' });
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ codigo: r.codigo, nombre: r.nombre, tipo: r.tipo, empresa_id: r.empresa_id, es_mandante: r.es_mandante, marca: r.marca, modelo: r.modelo, patente: r.patente, estado: r.estado });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.codigo.trim() || !form.nombre.trim() || !form.tipo) { toast.error('Complete codigo, nombre y tipo'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/catalogo-recursos/${editing.id}`, form, { headers });
        toast.success('Recurso actualizado');
      } else {
        await axios.post(`${API}/catalogo-recursos`, form, { headers });
        toast.success('Recurso creado');
      }
      setShowModal(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    setConfirmDelete({ onConfirm: async () => {
      setConfirmDelete(null);
      try { await axios.delete(`${API}/catalogo-recursos/${id}`, { headers }); toast.success('Eliminado'); fetchData(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    }});
  };

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-6" data-testid="catalogo-recursos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Catalogo de Recursos</h1>
          <p className="text-sm text-slate-500 mt-0.5">Vehiculos, maquinaria, herramientas e instrumentos</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] transition-colors" data-testid="nuevo-recurso-btn">
          <Plus className="w-4 h-4" />Nuevo Recurso
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por codigo, nombre, marca o patente..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20" data-testid="search-recursos" />
          </div>
          <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-tipo">
            <option value="">Todos los tipos</option>
            {TIPOS_RECURSO.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B919B]" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16"><Truck className="w-12 h-12 text-slate-300 mb-3" /><p className="text-sm text-slate-500">No hay recursos registrados</p></div>
        ) : (
          <div className="divide-y divide-slate-200">
            {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([tipo, items]) => {
              const isOpen = openGroups[tipo] === true;
              return (
                <div key={tipo}>
                  <button onClick={() => setOpenGroups(prev => ({ ...prev, [tipo]: !isOpen }))}
                    className="w-full bg-slate-50 px-4 py-2.5 flex items-center gap-2 hover:bg-slate-100 transition-colors">
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                    <Wrench className="w-4 h-4 text-[#a05e49]" />
                    <span className="text-sm font-semibold text-slate-700">{tipo}</span>
                    <span className="text-xs text-slate-400 ml-1">({items.length})</span>
                  </button>
                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="border-b border-slate-100">
                          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase">Codigo</th>
                          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase">Nombre</th>
                          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase">Empresa</th>
                          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase">Marca</th>
                          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase">Modelo</th>
                          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase">Patente</th>
                          <th className="text-center px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase">Estado</th>
                          <th className="w-20"></th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-50">
                          {items.map(r => (
                            <tr key={r.id} className="hover:bg-slate-50/50" data-testid={`recurso-${r.id}`}>
                              <td className="px-4 py-2.5 text-sm font-mono text-slate-600">{r.codigo}</td>
                              <td className="px-4 py-2.5 text-sm text-slate-800">{r.nombre}</td>
                              <td className="px-4 py-2.5 text-sm text-slate-500">{r.empresa_nombre}</td>
                              <td className="px-4 py-2.5 text-sm text-slate-500">{r.marca}</td>
                              <td className="px-4 py-2.5 text-sm text-slate-500">{r.modelo}</td>
                              <td className="px-4 py-2.5 text-sm font-mono text-slate-500">{r.patente}</td>
                              <td className="px-4 py-2.5 text-center">
                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ESTADO_COLORS[r.estado] || 'bg-slate-100 text-slate-500'}`}>{r.estado}</span>
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1 justify-end">
                                  <button onClick={() => openEdit(r)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => handleDelete(r.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-slate-900 mb-4">{editing ? 'Editar Recurso' : 'Nuevo Recurso'}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Codigo *</label>
                  <input type="text" value={form.codigo} onChange={e => u('codigo', e.target.value)} placeholder="CAT-793F-001"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-codigo" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Tipo *</label>
                  <select value={form.tipo} onChange={e => u('tipo', e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-tipo">
                    {TIPOS_RECURSO.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Nombre *</label>
                <input type="text" value={form.nombre} onChange={e => u('nombre', e.target.value)} placeholder="Camion Minero CAT 793F"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-nombre" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Empresa</label>
                <div className="flex items-center gap-3 mb-2">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={form.es_mandante} onChange={() => u('es_mandante', true)} className="w-4 h-4 text-[#1B919B]" />
                    <span className="text-sm text-slate-600">Lomas Bayas</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={!form.es_mandante} onChange={() => u('es_mandante', false)} className="w-4 h-4 text-[#1B919B]" />
                    <span className="text-sm text-slate-600">Contratista</span>
                  </label>
                </div>
                {!form.es_mandante && (
                  <select value={form.empresa_id || ''} onChange={e => u('empresa_id', e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-empresa">
                    <option value="">Seleccionar contratista</option>
                    {empresas.map(emp => <option key={emp.id} value={emp.id}>{emp.razon_social}</option>)}
                  </select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Marca</label>
                  <input type="text" value={form.marca} onChange={e => u('marca', e.target.value)} placeholder="Caterpillar"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-marca" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Modelo</label>
                  <input type="text" value={form.modelo} onChange={e => u('modelo', e.target.value)} placeholder="793F"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-modelo" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Patente</label>
                  <input type="text" value={form.patente} onChange={e => u('patente', e.target.value)} placeholder="ABCD-12"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-patente" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Estado</label>
                  <select value={form.estado} onChange={e => u('estado', e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="modal-estado">
                    {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm bg-[#a05e49] text-white rounded-lg hover:bg-[#8b4f3d]" data-testid="modal-save-btn">{editing ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      , document.body)}
      <ConfirmModal open={!!confirmDelete} title="Eliminar recurso" message="Esta seguro de eliminar este recurso?"
        confirmLabel="Eliminar" variant="danger" onConfirm={confirmDelete?.onConfirm} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
}
